import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import ServiceDetail from './ServiceDetailModal'
import GenericAddButton from '../components/GenericAddButton'
import ServiceAddModal from './ServiceAddModal'
import ServiceListModal from './ServiceListModal'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import GenericListFilter from './GenericListFilter'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericListFilter from '../hooks/useGenericListFilter'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import RefreshButton from '../components/RefreshButton'
import ServiceDeleteModal from './ServiceDeleteModal'
import deleteIcon from '../assets/images/icons/delete.png'
import detailIcon from '../assets/images/icons/detail.png'
import DetailButton from '../components/DetailButtonModal'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

const serviceDetailFields = [
  ['date', 'TRIPLOG.SERVICECOSTS.TABLE.date'],
  ['vehicleId', 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId'],
  ['spz', 'vehicle_license_plate'],
  ['serviceType', 'TRIPLOG.SERVICECOSTS.TABLE.serviceType'],
  ['kilometers', 'TRIPLOG.SERVICECOSTS.TABLE.kilometers'],
  ['description', 'TRIPLOG.SERVICECOSTS.TABLE.description'],
  ['laborCost', 'TRIPLOG.SERVICECOSTS.TABLE.laborCost'],
  ['materialCost', 'TRIPLOG.SERVICECOSTS.TABLE.materialCost'],
  ['totalCost', 'TRIPLOG.SERVICECOSTS.TABLE.totalCost'],
  ['workshopName', 'TRIPLOG.SERVICECOSTS.TABLE.workshopName'],
  ['workshopAddress', 'TRIPLOG.SERVICECOSTS.TABLE.workshopAddress'],
  ['documentNumber', 'TRIPLOG.SERVICECOSTS.TABLE.documentNumber'],
  ['note', 'TRIPLOG.SERVICECOSTS.TABLE.note'],
]

const DEFAULT_COLUMNS = [
  { key: 'date', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.date' },
  { key: 'vehicleId', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId' },
  { key: 'spz', labelKey: 'vehicle_license_plate' },
  { key: 'serviceType', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.serviceType' },
  { key: 'kilometers', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.kilometers' },
  { key: 'totalCost', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.totalCost' },
  { key: 'workshopName', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.workshopName' },
  { key: 'description', labelKey: 'TRIPLOG.SERVICECOSTS.TABLE.description' }
]

function SortableHeaderCell({
  col,
  orderBy,
  order,
  handleSort,
  colWidths,
  handleMouseDown,
  t
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: col.key })

  const customWidth = colWidths[col.key]

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(customWidth
      ? {
          width: customWidth,
          minWidth: customWidth,
          maxWidth: customWidth
        }
      : {
          flex: '1 1 0',
          minWidth: 0
        }),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8,
    boxSizing: 'border-box'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', marginRight: 6, color: '#999' }}
      >
        ⠿
      </span>

      <TableSortLabel
        active={orderBy === col.key}
        direction={orderBy === col.key ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {['laborCost', 'materialCost', 'totalCost'].includes(col.key)
  ? `${t(col.labelKey)} (€)`
  : t(col.labelKey)}
      </TableSortLabel>

      <div
        style={{
          position: 'absolute',
          right: 4,
          top: '20%',
          bottom: '20%',
          width: 1,
          background: '#9e9e9e'
        }}
      />

      <div
        onMouseDown={(e) => handleMouseDown(e, col.key)}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize'
        }}
      />
    </div>
  )
}

const ServiceBox = ({ user, t, layout, viewport }) => {
  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(true)
  const [servicesError, setServicesError] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
  const [selectedService, setSelectedService] = useState(null)
  const [serviceDetailOpen, setServiceDetailOpen] = useState(false)
  const [serviceListFullscreen, setServiceListFullscreen] = useState(false)
  const [hoveredService, setHoveredService] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
const [tooltipTimer, setTooltipTimer] = useState(null)
  const [openExportDropdown, setOpenExportDropdown] = useState(false)

const [deleteService, setDeleteService] = useState(null)
const [deleteModalOpen, setDeleteModalOpen] = useState(false)
const [addServiceOpen, setAddServiceOpen] = useState(false)

const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

const [orderBy, setOrderBy] = useState(() => {
  const stored = localStorage.getItem('serviceOrderBy')
  return stored || 'date'
})

const [order, setOrder] = useState(() => {
  const stored = localStorage.getItem('serviceOrder')
  return stored || 'desc'
})

const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem('serviceColumns')
  if (stored) return JSON.parse(stored)

  return DEFAULT_COLUMNS
})

const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem('serviceColWidths')
  return stored ? JSON.parse(stored) : {}
})

const sensors = useSensors(useSensor(PointerSensor))

const handleSort = (col) => {
  if (orderBy === col) {
    setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
  } else {
    setOrderBy(col)
    setOrder('asc')
  }
}

const handleMouseDown = (e, key) => {
  const startX = e.clientX
  const startWidth = colWidths[key] || 150

  const onMouseMove = (e) => {
    const delta = e.clientX - startX
    const newWidth = Math.max(80, startWidth + delta)
    setColWidths(prev => ({ ...prev, [key]: newWidth }))
  }

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const handleDragEnd = (event) => {
  const { active, over } = event
  if (!over || active.id === over.id) return

  const oldIndex = columns.findIndex(c => c.key === active.id)
  const newIndex = columns.findIndex(c => c.key === over.id)

  const reordered = [...columns]
  const [moved] = reordered.splice(oldIndex, 1)
  reordered.splice(newIndex, 0, moved)

  setColumns(reordered)
}

const [tooltipEnabled, setTooltipEnabled] = useState(() => {
  const stored = localStorage.getItem('tooltipEnabled')
  return stored === null ? false : stored === 'true'
})

const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

useEffect(() => {
  localStorage.setItem('tooltipEnabled', tooltipEnabled)
}, [tooltipEnabled])

useEffect(() => {
  localStorage.setItem('serviceColumns', JSON.stringify(columns))
}, [columns])

useEffect(() => {
  localStorage.setItem('serviceOrderBy', orderBy)
}, [orderBy])

useEffect(() => {
  localStorage.setItem('serviceOrder', order)
}, [order])

useEffect(() => {
  localStorage.setItem('serviceColWidths', JSON.stringify(colWidths))
}, [colWidths])

  const loadServices = useCallback(async (opts = {}) => {
  const { silent = false } = opts
    try {
      if (!silent) setServicesLoading(true)
      setServicesError(null)

      const res = await fetchWithAuth('/triplog/service-costs')

      if (!res.ok) {
        setServicesError('Nepodarilo sa načítať servisy.')
        return
      }

const data = await res.json()

if (Array.isArray(data) && data.length > 0) {

}

setServices(Array.isArray(data) ? data : [])
    } catch {
      setServicesError('Chyba pripojenia pri načítaní servisov.')
    } finally {
      if (!silent) setServicesLoading(false)
    }
  }, [])

const handleServiceSaved = useCallback(() => {
  loadServices()
}, [loadServices])

const handleDeleteServiceClick = useCallback((service) => {
  setDeleteService(service)
  setDeleteModalOpen(true)
}, [])

  const loadVehicles = useCallback(async () => {
    if (!user?.id) return

    try {
      const res = await fetchWithAuth(`/users/${user.id}/vehicles`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      const index = data.reduce((acc, vehicle) => {
        if (vehicle?.id == null) return acc
        acc[String(vehicle.id)] = vehicle
        return acc
      }, {})

      setVehiclesById(index)
    } catch {
      // vehicle names will fallback to nested service payload or '-'
    }
  }, [user?.id])

  useEffect(() => {
    loadServices()
  }, [loadServices])

  useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

  const getServiceVehicleLabel = useCallback((service) => {
    const vehicleFromList = vehiclesById[String(service?.vehicleId)]
    const vehicle = vehicleFromList || service?.vehicle || service?.serviceVehicle
    return vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || '-'
  }, [vehiclesById])

const getServiceVehicleSpz = useCallback((service) => {
  const vehicleFromList = vehiclesById[String(service?.vehicleId)]
  const vehicle = vehicleFromList || service?.vehicle || service?.serviceVehicle
  return vehicle?.licensePlate || vehicle?.spz || '-'
}, [vehiclesById])

const getServiceFilterData = useCallback((service) => ({
  vehicleId: service?.vehicleId,
  date: service?.date
}), [])

const {
  criteria: filterCriteria,
  setCriteria: setFilterCriteria,
  filteredItems: filteredServices
} = useGenericListFilter(services, getServiceFilterData)

const isFiltered = Object.values(filterCriteria || {}).some(v => v && v !== '')

const vehicleOptions = useMemo(() => {
  return Object.values(vehiclesById).map((vehicle) => {
    const name = vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || `Vozidlo #${vehicle?.id}`
    const plate = vehicle?.licensePlate || vehicle?.spz || ''

    return {
      value: String(vehicle?.id),
      label: plate && name !== plate ? `${name} (${plate})` : name
    }
  })
}, [vehiclesById])

const getServiceSearchData = useCallback((service) => {
  const vehicleFromList = vehiclesById[String(service?.vehicleId)]
  const vehicle = vehicleFromList || service?.vehicle || service?.serviceVehicle

  return {
    ...service,
    vehicleName: vehicle?.name,
    vehicleModel: vehicle?.model,
    vehicleLicensePlate: vehicle?.licensePlate,
    vehicleSpz: vehicle?.spz,
    searchDate: service?.date ? new Date(service.date).toLocaleDateString() : ''
  }
}, [vehiclesById])

const searchFilteredServices = useGenericTextSearch(
  filteredServices,
  searchQuery,
  getServiceSearchData
)

const filterLabels = useMemo(() => ({
  allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
  from: t('TRIPLOG.FILTER_FROM'),
  to: t('TRIPLOG.FILTER_TO'),
  month: t('TRIPLOG.FILTER_MONTH'),
  year: t('TRIPLOG.FILTER_YEAR'),
  clear: t('TRIPLOG.BUTTON.CLEAR')
}), [t])

const sortedServices = [...searchFilteredServices].sort((a, b) => {
  let aVal = a[orderBy]
  let bVal = b[orderBy]

  if (orderBy === 'vehicleId') {
    aVal = getServiceVehicleLabel(a)
    bVal = getServiceVehicleLabel(b)
  }

if (orderBy === 'spz') {
  aVal = getServiceVehicleSpz(a)
  bVal = getServiceVehicleSpz(b)
}

  if (orderBy === 'date') {
    aVal = aVal ? new Date(aVal).getTime() : 0
    bVal = bVal ? new Date(bVal).getTime() : 0
  }

  if (
    orderBy === 'kilometers' ||
    orderBy === 'laborCost' ||
    orderBy === 'materialCost' ||
    orderBy === 'totalCost'
  ) {
    aVal = Number(aVal) || 0
    bVal = Number(bVal) || 0
  }

  if (typeof aVal === 'string') aVal = aVal.toLowerCase()
  if (typeof bVal === 'string') bVal = bVal.toLowerCase()

  if (aVal > bVal) return order === 'asc' ? 1 : -1
  if (aVal < bVal) return order === 'asc' ? -1 : 1
  return 0
})

const selectedServiceIndex = sortedServices.findIndex(
  (item) => item?.id === selectedService?.id
)

const handlePrevService = () => {
  if (selectedServiceIndex <= 0) return
  const prevService = sortedServices[selectedServiceIndex - 1]
  setSelectedService(prevService)
  setActiveRow(prevService?.id ?? null)
}

const handleNextService = () => {
  if (selectedServiceIndex < 0 || selectedServiceIndex >= sortedServices.length - 1) return
  const nextService = sortedServices[selectedServiceIndex + 1]
  setSelectedService(nextService)
  setActiveRow(nextService?.id ?? null)
}

  const detailColWidth = viewport <= 900 ? 110 : 50

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #1e3f44',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}
      >

<GenericAddButton
  onToggle={() => setAddServiceOpen(true)}
/>

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

<FilterToggleButton
  visible={showFilter}
  active={isFiltered}
  onToggle={() => setShowFilter(prev => !prev)}
/>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            fontSize: 13,
            userSelect: 'none',
    	    marginTop: 5
          }}
        >
          <input
            type="checkbox"
            checked={tooltipEnabled}
            onChange={(e) => setTooltipEnabled(e.target.checked)}
            style={{
              cursor: 'pointer',
              accentColor: '#06857a',
              appearance: 'none',
              width: 16,
              height: 16,
              border: '1px solid #1e3f44',
              borderRadius: 3,
              background: tooltipEnabled ? '#2da9a4' : '#fff'
            }}
          />
          {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
        </label>

<RefreshButton onClick={() => loadServices({ silent: true })} />

<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !== JSON.stringify(DEFAULT_COLUMNS.map(c => c.key)) ||
    orderBy !== 'date' ||
    order !== 'desc'
  }
  onReset={() => {
const resetCols = DEFAULT_COLUMNS

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('date')
    setOrder('desc')

localStorage.removeItem('serviceColumns')
localStorage.removeItem('serviceColWidths')
localStorage.removeItem('serviceOrderBy')
localStorage.removeItem('serviceOrder')
  }}
/>

<button
  onMouseEnter={(e)=>{
    e.currentTarget.style.background='#2da9a4'
  }}
  onMouseLeave={(e)=>{
    e.currentTarget.style.background='#f5f5f5'
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e)=>{
    e.currentTarget.style.transform='scale(0.96)'
    e.currentTarget.style.boxShadow='0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e)=>{
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onClick={(e)=>{
    e.currentTarget.blur()
    setOpenExportDropdown(true)
    setServiceListFullscreen(true)
  }}
  style={{
    background:'#f5f5f5',
    border:'1px solid #1e3f44',
    borderRadius:6,
    height:24,
    padding:'0 10px',
    cursor:'pointer',
    fontWeight:700,
    boxShadow:'0 1px 8px rgba(0,0,0,0.30)',
    transition:'transform 0.05s ease, box-shadow 0.1s ease',
    marginTop:5
  }}
>
  {t('TRIPLOG.BUTTON_EXPORT')}
</button>

<button
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(true)
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = 'none'
  }}
  onClick={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    e.currentTarget.blur()
    setServiceListFullscreen(true)
  }}
  style={{
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 32
  }}
>
  ⛶
</button>
      </div>

      <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
        {t('TRIPLOG.TAB.SERVIS')}
      </h3>

      {servicesLoading && services.length === 0 && <div>Načítavam...</div>}
      {servicesError && <div style={{ color: 'red' }}>{servicesError}</div>}

{showFilter && (
  <GenericListFilter
    value={filterCriteria}
    onChange={setFilterCriteria}
    vehicles={vehicleOptions}
    labels={filterLabels}
  />
)}

<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    minHeight: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }}
>
<DndContext
  sensors={sensors}
  collisionDetection={closestCenter}
  onDragEnd={handleDragEnd}
>
  <SortableContext
    items={columns.map(c => c.key)}
    strategy={horizontalListSortingStrategy}
  >
    <div
      style={{
        display: 'flex',
        background: '#c8e1e1',
        padding: '8px 12px',
        fontWeight: 700,
        fontSize: layout.vehicleHeaderFontSize
      }}
    >
      {columns.map(col => (
        <SortableHeaderCell
          key={col.key}
          col={col}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          colWidths={colWidths}
          handleMouseDown={handleMouseDown}
	  t={t}
        />
      ))}

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.SERVICECOSTS.DETAILS'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={detailIcon}
    alt="detail"
    style={{ width: 22, height: 22 }}
  />
</div>

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.SERVICECOSTS.DELETE_RECORD'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  marginRight: -6,
  marginLeft: -6
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 22,
      height: 22
    }}
  />
</div>

    </div>
  </SortableContext>
</DndContext>
<div
  style={{
    overflow: 'auto',
    flex: 1,
    minHeight: 0
  }}
>
{sortedServices.map(s => (
  <div
    key={s.id}
    onClick={() => {
      setActiveRow(s.id)
      setSelectedService(s)
      setServiceDetailOpen(true)
    }}
    onMouseEnter={(e) => {
      if (e.target.closest('.service-detail-button')) return
      setHoveredService(s)
    }}
    onMouseMove={(e) => {
      if (e.target.closest('.service-detail-button')) {
        setHoveredService(null)
      } else {
        setHoveredService(s)
      }
    }}
    onMouseLeave={(e) => {
      const related = e.relatedTarget
      if (related && related.closest && related.closest('.service-tooltip')) return
      setHoveredService(null)
    }}
    style={{
      display: 'flex',
      padding: '6px 2px 6px 12px',
      borderTop: '1px solid #ececec',
      alignItems: 'center',
      backgroundColor:
        activeRow === s.id
          ? '#e6f4f1'
          : hoveredService?.id === s.id
            ? '#f3fafa'
            : '#fff',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      fontSize: layout.vehicleRowFontSize
    }}
  >
    {columns.map(col => (
      <div
        key={col.key}
        style={{
          ...(colWidths[col.key]
            ? {
                width: colWidths[col.key],
                minWidth: colWidths[col.key],
                maxWidth: colWidths[col.key]
              }
            : {
                flex: '1 1 0',
                minWidth: 0
              }),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
{col.key === 'vehicleId'
  ? getServiceVehicleLabel(s)
  : col.key === 'spz'
    ? getServiceVehicleSpz(s)
    : col.key === 'date'
            ? (s.date
                ? new Date(s.date).toLocaleDateString('sk-SK')
                : '-')
      : col.key === 'totalCost'
        ? (s.totalCost != null ? Number(s.totalCost).toFixed(2) : '-')
        : s[col.key] || '-'}
      </div>
    ))}

    <div style={{ flex: `0 0 ${detailColWidth}px`, display: 'flex', justifyContent: 'center' }}>
<DetailButton
  onClick={(e) => {
    e.stopPropagation()
    setSelectedService(s)
    setServiceDetailOpen(true)
  }}
/>
    </div>
<div style={{
  flex: `0 0 ${detailColWidth}px`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: -5
}}>
<button
  style={{
    border: '1.5px solid #a33',
    background: '#f5f5f5',
    borderRadius: 6,
    height: 28,
    width: 35,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
    transition: 'transform 0.05s ease, box-shadow 0.1s ease',
    outline: 'none'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#f3bcbc'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
    e.currentTarget.blur()
  }}
  onClick={(e) => {
    e.stopPropagation()
    handleDeleteServiceClick(s)
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 19,
      height: 19,
      pointerEvents: 'none'
    }}
  />
</button>
</div>
  </div>
))}
      </div>
      </div>

      {tooltipEnabled && hoveredService && (
        <div
          className="service-tooltip"
          onMouseLeave={() => setHoveredService(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 1920 ? 9 :
                  viewport >= 1600 ? 9 :
                  viewport >= 1366 ? 10 : 20,
                bottom:
                  viewport >= 1920 ? 50 :
                  viewport >= 1600 ? 50 :
                  viewport >= 1366 ? 49 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f3fafa',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
	{serviceDetailFields.map(([key, labelKey]) => (
 	 <div key={key} style={{ marginBottom: 4, display: 'flex' }}>
<strong style={{ minWidth: 220 }}>
  {['laborCost', 'materialCost', 'totalCost'].includes(key)
    ? `${t(labelKey)} (€):`
    : `${t(labelKey)}:`}
</strong>
  	  <span style={{ color: '#1e3f44', fontWeight: 600 }}>
{key === 'vehicleId'
  ? getServiceVehicleLabel(hoveredService)
  : key === 'spz'
    ? getServiceVehicleSpz(hoveredService)
  : key === 'date'
    ? (hoveredService.date
        ? new Date(hoveredService.date).toLocaleDateString('sk-SK')
        : '-')
  : ['laborCost', 'materialCost', 'totalCost'].includes(key)
    ? (hoveredService[key] != null
        ? Number(hoveredService[key]).toFixed(2)
        : '-')
  : hoveredService[key] || '-'}
  	  </span>
  	</div>
	))}
        </div>
      )}

{showFullscreenHint && (
  <div
          style={{
            position: 'fixed',
    	    top: cursorPos.y - 50,
            left: cursorPos.x,
     	    transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #1e3f44',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
  >
    {typeof showFullscreenHint === 'string'
  ? showFullscreenHint
  : t('MODAL_DETAILS')}
  </div>
)}

<ServiceListModal
  open={serviceListFullscreen}
  openExportDropdown={openExportDropdown}
  onClose={() => {
    setServiceListFullscreen(false)
    setOpenExportDropdown(false)
  }}
  services={services}
  servicesLoading={servicesLoading}
  servicesError={servicesError}
  filterCriteria={filterCriteria}
  setFilterCriteria={setFilterCriteria}
/>

      <ServiceDetail
        open={serviceDetailOpen}
        onClose={() => {
          setServiceDetailOpen(false)
          setSelectedService(null)
          setActiveRow(null)
        }}
        service={selectedService}
  	onSaved={handleServiceSaved}
        onPrevService={handlePrevService}
        onNextService={handleNextService}
        hasPrevService={selectedServiceIndex > 0}
        hasNextService={selectedServiceIndex >= 0 && selectedServiceIndex < sortedServices.length - 1}
      />
<ServiceDeleteModal
  open={deleteModalOpen}
  service={deleteService}
  onClose={() => {
    setDeleteModalOpen(false)
    setDeleteService(null)
  }}
  onDeleted={() => {
    setDeleteModalOpen(false)
    setDeleteService(null)
    loadServices()
  }}
  t={t}
/>
<ServiceAddModal
  open={addServiceOpen}
  onClose={() => setAddServiceOpen(false)}
  onSaved={() => {
    setAddServiceOpen(false)
    handleServiceSaved()
  }}
  userId={user?.id}
/>
    </div>
  )
}

export default ServiceBox